import {
  fetchAvailableOperations,
  fetchTargetOperations,
} from '@/features/authentication/api/permissions';
import {
  AVAILABLE_OPERATIONS_KEY,
  PERMISSIONS_STALE_TIME,
  TARGET_OPERATIONS_KEY,
} from '@/features/authentication/config/constants';
import { batchPermissionTargets } from '@/features/authentication/lib/authorizationCapabilities';
import { useAuthStore } from '@/features/authentication/stores/authStore';
import type {
  PermissionTarget,
  PermissionTargetRef,
  TargetOperationsResponse,
} from '@/features/authentication/types/permissions';
import { queryOptions, useQueries, useQuery } from '@tanstack/react-query';

function availableOperationsKey(uid: string | undefined) {
  return [...AVAILABLE_OPERATIONS_KEY, uid] as const;
}

export function availableOperationsQuery(uid: string | undefined) {
  return queryOptions({
    queryKey: availableOperationsKey(uid),
    queryFn: ({ signal }) => fetchAvailableOperations({ signal }),
    enabled: !!uid,
    staleTime: PERMISSIONS_STALE_TIME,
  });
}

export function useAvailableOperations() {
  const uid = useAuthStore((state) => state.user?.uid);
  return useQuery(availableOperationsQuery(uid));
}

function targetOperationsKey(uid: string | undefined, targets: readonly PermissionTarget[]) {
  return [...TARGET_OPERATIONS_KEY, uid, targets.map((target) => target.key)] as const;
}

export function targetOperationsQuery(
  uid: string | undefined,
  targets: readonly PermissionTarget[],
) {
  return queryOptions({
    queryKey: targetOperationsKey(uid, targets),
    queryFn: ({ signal }) => fetchTargetOperations(targets, { signal }),
    enabled: !!uid && targets.length > 0,
    staleTime: PERMISSIONS_STALE_TIME,
  });
}

function mergeTargetResponses(
  responses: readonly TargetOperationsResponse[],
): TargetOperationsResponse['targets'] | undefined {
  const first = responses[0];
  if (!first) return {};
  if (
    responses.some(
      (response) =>
        response.policyVersion !== first.policyVersion ||
        response.principalVersion !== first.principalVersion,
    )
  ) {
    return undefined;
  }
  return Object.assign({}, ...responses.map((response) => response.targets));
}

export function useTargetOperations(targets: readonly PermissionTargetRef[]) {
  const uid = useAuthStore((state) => state.user?.uid);
  const batches = batchPermissionTargets(targets);
  const queries = useQueries({
    queries: batches.map((batch) => targetOperationsQuery(uid, batch)),
  });
  const responses = queries.flatMap((query) => (query.data ? [query.data] : []));
  const complete = responses.length === batches.length;
  const mergedTargets = complete ? mergeTargetResponses(responses) : undefined;
  const versionMismatch = complete && mergedTargets === undefined;

  return {
    targets: mergedTargets,
    isPending: batches.length > 0 && queries.some((query) => query.isPending),
    isError: versionMismatch || queries.some((query) => query.isError),
    refetch: () => {
      for (const query of queries) void query.refetch();
    },
  };
}
